import React, { useState } from 'react'

import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  InsertCoverageProviderForMonthRequest,
  SaveCoverageProviderRequest,
  insertCoverageProviderForMonth,
  saveCoverageProvider,
} from 'core'

import { Button, Card, Col, DefaultRadioGroupV2, Row, Separator } from 'common/components'
import { formatShortMonthYear } from 'common/dateFormatters'
import { useCommandRequestV2 } from 'common/hooks'

import styles from './index.module.css'

type ConfirmAAttestationForMonthProps = {
  startDate: string
  providerName: string
  policyName: string
  onSaved?: () => void
  onNotConfirmed?: (startDate: string) => void
}

type InitialForm = {
  answer?: boolean
}
const ConfirmAAttestationForMonth = ({
  startDate,
  providerName,
  policyName,
  onSaved = () => {},
  onNotConfirmed = () => {},
}: ConfirmAAttestationForMonthProps) => {
  const [initialValues] = useState<InitialForm>({
    answer: null,
  })

  const { sendCommand, isLoading } = useCommandRequestV2((request: InsertCoverageProviderForMonthRequest) =>
    insertCoverageProviderForMonth(request),
  )

  const onSubmit = async (values: { answer: boolean }) => {
    if (values.answer) {
      const result = await sendCommand({
        providerName: providerName,
        startDate,
        attestation: true,
        policyName: policyName,
      })

      if (result.completed) {
        onSaved()
      }
    } else {
      onNotConfirmed(startDate)
    }
  }

  return (
    <Card className={styles.wrap}>
      <Row gutter={16}>
        <Col>
          <div className={styles.icon}>
            <i className="fad fa-exclamation-triangle"></i>
          </div>
        </Col>

        <Col>
          <h4>
            <b>Attest to your insurance coverage for {formatShortMonthYear(startDate)}</b>
          </h4>
        </Col>
      </Row>

      <p className={styles.paragraph}>
        Please attest that you have qualifying individual health insurance coverage for the selected month.
      </p>

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={formValidator}
        onSubmit={onSubmit}
        validate={(values) => {
          return {}
        }}
      >
        {(formik) => (
          <form autoComplete="off">
            <div className={'mb-16'}>
              <p>
                Are you covered by{' '}
                <b>
                  {providerName} {policyName}
                </b>{' '}
                for <b>{formatShortMonthYear(startDate)}</b>?
              </p>

              <DefaultRadioGroupV2
                name={'answer'}
                direction={'horizontal'}
                items={[
                  { name: 'Yes', value: true },
                  { name: 'No', value: false },
                ]}
              ></DefaultRadioGroupV2>
            </div>

            <div className={styles.separatorWrap}>
              <Separator />
            </div>

            <Row gutter={8} justify={'end'}>
              <Col>
                <Button
                  type={'primary'}
                  onClick={() => formik.handleSubmit()}
                  loading={isLoading}
                  disabled={!formValidator.isValidSync(formik.values) || isLoading}
                >
                  Confirm
                </Button>
              </Col>
            </Row>
          </form>
        )}
      </Formik>
    </Card>
  )
}

const formValidator = Yup.object({
  answer: Yup.bool().nullable().required('Required'),
})

export default ConfirmAAttestationForMonth
